/**
 * Frame-quality pre-checks for the guided capture (components/GuidedCapture.js).
 *
 * Two questions, asked of a small downsampled copy of the photograph before it
 * is uploaded:
 *
 *   hand           is skin coming into the frame from its edges?
 *   foreignObject  is there a small, round, grey-metallic object (a coin)?
 *
 * Both answers are HEURISTICS and the UI treats them that way: a gentle,
 * non-blocking warning, never a refusal.
 *
 * Skin. A pixel is skin-toned when both the Kovac et al. RGB rule and the
 * Chai & Ngan YCbCr box accept it (the same pair backend/ai/segmentation.py
 * uses). That also accepts tan caps and brown soil, so the colour alone decides
 * nothing. What separates a hand is WHERE the colour is: a hand enters the frame
 * from one or two sides, a tan cap sits in the middle, and a skin-toned
 * background (leaf litter, soil) reaches every side. So: skin on at least one
 * side band, on fewer than three, and covering a plausible share of the frame.
 *
 * Coins. A connected region of bright, low-saturation pixels that is small
 * (0.2-3% of the frame), about as wide as tall, fills roughly a circle's share
 * of its bounding box (a circle fills pi/4, about 0.79) and does not touch the
 * edge. It does NOT find copper or brass coins, which are not grey, and a small
 * white button mushroom seen from above can look exactly like one.
 */

export const ANALYSIS_SIDE = 96;
export const EDGE_BAND = 0.12;
export const SIDE_SKIN_FRACTION = 0.2;
export const HAND_MIN_FRACTION = 0.03;
export const HAND_MAX_FRACTION = 0.5;
export const COIN_MIN_FRACTION = 0.002;
export const COIN_MAX_FRACTION = 0.03;
const ANALYSIS_TIMEOUT_MS = 4000;

export function isSkin(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const kovac = r > 95 && g > 40 && b > 20 && max - min > 15 && Math.abs(r - g) > 15 && r > g && r > b;
  if (!kovac) return false;
  const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
  const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
  return cb >= 77 && cb <= 127 && cr >= 133 && cr <= 173;
}

export function isMetallic(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const value = max / 255;
  const saturation = max === 0 ? 0 : (max - min) / max;
  return value >= 0.55 && saturation <= 0.18;
}

function roundMetallicObjects(mask, width, height) {
  const seen = new Uint8Array(mask.length);
  const total = width * height;
  let found = 0;
  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || seen[start]) continue;
    const stack = [start];
    seen[start] = 1;
    let area = 0;
    let minX = width; let maxX = -1; let minY = height; let maxY = -1;
    while (stack.length) {
      const i = stack.pop();
      const x = i % width;
      const y = (i - x) / width;
      area += 1;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      const neighbours = [x > 0 ? i - 1 : -1, x < width - 1 ? i + 1 : -1, y > 0 ? i - width : -1, y < height - 1 ? i + width : -1];
      for (const n of neighbours) {
        if (n >= 0 && mask[n] && !seen[n]) { seen[n] = 1; stack.push(n); }
      }
    }
    const boxW = maxX - minX + 1;
    const boxH = maxY - minY + 1;
    const fraction = area / total;
    const touchesEdge = minX === 0 || minY === 0 || maxX === width - 1 || maxY === height - 1;
    const aspect = Math.min(boxW, boxH) / Math.max(boxW, boxH);
    const fill = area / (boxW * boxH);
    if (!touchesEdge && boxW >= 3 && boxH >= 3 && fraction >= COIN_MIN_FRACTION && fraction <= COIN_MAX_FRACTION
        && aspect >= 0.7 && fill >= 0.6 && fill <= 0.92) {
      found += 1;
    }
  }
  return found;
}

/** Analyse an ImageData-shaped frame ({data, width, height}, RGBA). Pure. */
export function analyzeFrame(frame) {
  const { data, width, height } = frame;
  const total = width * height;
  const bandX = Math.max(1, Math.round(width * EDGE_BAND));
  const bandY = Math.max(1, Math.round(height * EDGE_BAND));
  const sides = { top: [0, 0], bottom: [0, 0], left: [0, 0], right: [0, 0] };
  const metallic = new Uint8Array(total);
  let skin = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      const r = data[i * 4]; const g = data[i * 4 + 1]; const b = data[i * 4 + 2];
      const s = isSkin(r, g, b) ? 1 : 0;
      skin += s;
      if (y < bandY) { sides.top[0] += s; sides.top[1] += 1; }
      if (y >= height - bandY) { sides.bottom[0] += s; sides.bottom[1] += 1; }
      if (x < bandX) { sides.left[0] += s; sides.left[1] += 1; }
      if (x >= width - bandX) { sides.right[0] += s; sides.right[1] += 1; }
      metallic[i] = isMetallic(r, g, b) ? 1 : 0;
    }
  }

  const sideFractions = Object.fromEntries(Object.entries(sides).map(([k, [n, d]]) => [k, d ? n / d : 0]));
  const skinSides = Object.values(sideFractions).filter(f => f >= SIDE_SKIN_FRACTION).length;
  const skinFraction = total ? skin / total : 0;
  const hand = skinSides >= 1 && skinSides < 3 && skinFraction >= HAND_MIN_FRACTION && skinFraction <= HAND_MAX_FRACTION;
  const coins = roundMetallicObjects(metallic, width, height);
  const foreignObject = coins > 0;
  return {
    skinFraction: Math.round(skinFraction * 1000) / 1000,
    sideFractions,
    skinSides,
    roundMetallicObjects: coins,
    hand,
    foreignObject,
    warn: hand || foreignObject,
  };
}

/** A downsampled ImageData of an image file, or null where the browser cannot decode one. */
export async function frameFromFile(file, side = ANALYSIS_SIDE) {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
  const url = URL.createObjectURL(file);
  try {
    const image = await Promise.race([
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      }),
      new Promise((resolve) => setTimeout(() => resolve(null), ANALYSIS_TIMEOUT_MS)),
    ]);
    if (!image) return null;
    const naturalW = image.naturalWidth || image.width;
    const naturalH = image.naturalHeight || image.height;
    if (!naturalW || !naturalH) return null;
    const scale = Math.min(1, side / Math.max(naturalW, naturalH));
    const w = Math.max(1, Math.round(naturalW * scale));
    const h = Math.max(1, Math.round(naturalH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  } catch {
    return null;
  } finally {
    if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
  }
}

/** The analysis of a photograph file, or null when it could not be looked at (never throws). */
export async function checkFrameQuality(file) {
  try {
    const frame = await frameFromFile(file);
    return frame ? analyzeFrame(frame) : null;
  } catch {
    return null;
  }
}
